'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface ContactoForm {
  nombre: string;
  correo: string;
  telefono: string;
  mensaje: string;
}

export default function ContactoSection() {
  const [enviado, setEnviado] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactoForm>();

  const onSubmit = async (data: ContactoForm) => {
    setLoading(true);
    // Backend integration point: POST /api/prospectos with data
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setEnviado(true);
    toast.success('¡Mensaje enviado! Te contactaremos pronto.');
    reset();
    setTimeout(() => setEnviado(false), 5000);
  };

  return (
    <section id="contacto" className="py-20 beach-section">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-accent text-xs font-semibold tracking-widest uppercase mb-3">
              Contacto
            </p>
            <h2 className="font-display text-section-title font-bold text-primary mb-4">
              ¿Te interesa ser parte?
            </h2>
            <p className="text-muted-foreground text-lg">
              Dejanos tus datos y un administrador te contactará para explicarte el modelo de copropiedad y disponibilidad actual.
            </p>
          </div>

          <div className="bg-card rounded-2xl shadow-card border border-border p-8">
            {enviado ? (
              <div className="text-center py-8 fade-in">
                <CheckCircle size={52} className="text-success mx-auto mb-4" />
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  ¡Gracias por tu interés!
                </h3>
                <p className="text-muted-foreground">
                  Recibimos tu mensaje. Un administrador de Bahía Baliz te escribirá en las próximas horas.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Nombre */}
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-foreground mb-1.5">
                      Nombre completo <span className="text-destructive">*</span>
                    </label>
                    <input
                      id="nombre"
                      type="text"
                      placeholder="Ej: María Rodríguez"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
                        errors.nombre ? 'border-destructive' : 'border-border'
                      }`}
                      {...register('nombre', { required: 'Tu nombre es requerido' })}
                    />
                    {errors.nombre && (
                      <p className="text-destructive text-xs mt-1">{errors.nombre.message}</p>
                    )}
                  </div>

                  {/* Correo */}
                  <div>
                    <label htmlFor="correo" className="block text-sm font-medium text-foreground mb-1.5">
                      Correo electrónico <span className="text-destructive">*</span>
                    </label>
                    <input
                      id="correo"
                      type="email"
                      placeholder="tu@correo.com"
                      className={`w-full px-4 py-2.5 rounded-lg border bg-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${
                        errors.correo ? 'border-destructive' : 'border-border'
                      }`}
                      {...register('correo', {
                        required: 'El correo es requerido',
                        pattern: { value: /^\S+@\S+\.\S+$/, message: 'Correo inválido' },
                      })}
                    />
                    {errors.correo && (
                      <p className="text-destructive text-xs mt-1">{errors.correo.message}</p>
                    )}
                  </div>
                </div>

                {/* Teléfono */}
                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-foreground mb-1.5">
                    Teléfono / WhatsApp
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">Opcional. Incluí el código de país si es fuera de Costa Rica.</p>
                  <input
                    id="telefono"
                    type="tel"
                    placeholder="+506 8800-0000"
                    className="w-full px-4 py-2.5 rounded-lg border border-border bg-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                    {...register('telefono')}
                  />
                </div>

                {/* Mensaje */}
                <div>
                  <label htmlFor="mensaje" className="block text-sm font-medium text-foreground mb-1.5">
                    Mensaje <span className="text-destructive">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">Contanos brevemente tu interés o consulta.</p>
                  <textarea
                    id="mensaje"
                    rows={4}
                    placeholder="Me interesa conocer el modelo de copropiedad, cupos disponibles y precios..."
                    className={`w-full px-4 py-2.5 rounded-lg border bg-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors resize-none ${
                      errors.mensaje ? 'border-destructive' : 'border-border'
                    }`}
                    {...register('mensaje', { required: 'El mensaje es requerido', minLength: { value: 10, message: 'Mínimo 10 caracteres' } })}
                  />
                  {errors.mensaje && (
                    <p className="text-destructive text-xs mt-1">{errors.mensaje.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-60 transition-all scale-active"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Enviar mensaje
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}